import { useState, useRef, useEffect, useCallback } from "react";
import { unstable_batchedUpdates } from "react-dom";
import axios from "../axios";
import * as url from "../utils/url";
import {
  TRANSLATE_ERROR_GENERIC,
  TRANSLATE_MODE_ENGLISH,
  TRANSLATE_MODE_ORIGINAL,
  TRANSLATE_OUTCOME_CANCELLED,
  TRANSLATE_OUTCOME_DONE,
  TRANSLATE_OUTCOME_ERROR,
  TRANSLATE_OUTCOME_REVERT,
  TRANSLATE_REQUEST_TIMEOUT_MS,
  TRANSLATE_STATUS_DONE,
  TRANSLATE_STATUS_ERROR,
  TRANSLATE_STATUS_IDLE,
  TRANSLATE_STATUS_RUNNING,
  TRANSLATE_WINDOW_DEBOUNCE_MS,
  createEnglishCache,
  createRunState,
  isWindowSettled,
  mergeRead,
  mergeRows,
  pickMessageText,
  pickOptionText,
  pickQuestionText,
  pickScaleLabel,
  pickText,
  runWindowPass,
  signatureOf,
} from "../utils/translateResponsesCore";

const EMPTY_PROGRESS = { notice: null };

const unwrapData = (res) => (res && res.data ? res.data.data : null);

const toIdList = (resultIds) =>
  (Array.isArray(resultIds) ? resultIds : [])
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map((id) => String(id));

// Results "English" state for one Trivvy (By User: Messages and Survey/KC). Nothing is fetched
// while the page stays on Original. In English every row shows at once - cached English where it
// exists, the original otherwise - and one scheduler asks for the rows on screen plus one screen
// of lookahead. After each 0.4 s scroll pause (or when rows change) the scheduler moves to the
// new window; work for rows scrolled past is never sent, a request already sent finishes and its
// answers still land by result id + question id. Rows already settled on this page are never
// requested again. Leaving English, or the page, stops scheduling.
//
// resultIds: the rows on screen, in display order. getWindowIds(): the visible + lookahead ids
// (falls back to the first rows when the table cannot be measured).
const useTranslatedResponses = ({ triviaId, resultIds, getWindowIds }) => {
  const [mode, setModeState] = useState(TRANSLATE_MODE_ORIGINAL);
  const [status, setStatus] = useState(TRANSLATE_STATUS_IDLE);
  const [progress, setProgress] = useState(EMPTY_PROGRESS);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);

  const cacheRef = useRef(createEnglishCache());
  // One English session = one scheduler; leaving English or the page ends it.
  const sessionRef = useRef(0);
  const runningRef = useRef(false);
  const mountedRef = useRef(true);
  const timersRef = useRef([]);
  const debounceRef = useRef(null);
  const triviaIdRef = useRef(triviaId);
  const modeRef = useRef(TRANSLATE_MODE_ORIGINAL);
  const idsRef = useRef([]);
  const getWindowIdsRef = useRef(getWindowIds);
  // The window the scheduler works on next (the latest one after a scroll pause).
  const wantedRef = useRef([]);

  idsRef.current = toIdList(resultIds);
  getWindowIdsRef.current = getWindowIds;
  const rowsSignature = signatureOf(idsRef.current);

  const currentWindowIds = useCallback(() => {
    const picker = getWindowIdsRef.current;
    const picked = typeof picker === "function" ? toIdList(picker()) : [];
    if (picked.length > 0) {
      return picked;
    }
    return idsRef.current.slice(0, 50);
  }, []);

  const clearTimers = useCallback(() => {
    const pending = timersRef.current;
    timersRef.current = [];
    pending.forEach((entry) => {
      clearTimeout(entry.id);
      entry.resolve();
    });
  }, []);

  const wait = useCallback((ms) => {
    return new Promise((resolve) => {
      const entry = { id: null, resolve: resolve };
      entry.id = setTimeout(() => {
        timersRef.current = timersRef.current.filter((item) => item !== entry);
        resolve();
      }, ms);
      timersRef.current.push(entry);
    });
  }, []);

  // Ends the English session: no new requests; a request already sent may still finish.
  const endSession = useCallback(() => {
    sessionRef.current += 1;
    runningRef.current = false;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    clearTimers();
  }, [clearTimers]);

  const setModeBoth = useCallback((next) => {
    modeRef.current = next;
    setModeState(next);
  }, []);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      endSession();
    };
  }, [endSession]);

  // A different Trivvy starts clean: no scheduler, no cache, back to Original.
  useEffect(() => {
    if (triviaIdRef.current === triviaId) {
      return;
    }
    triviaIdRef.current = triviaId;
    endSession();
    cacheRef.current = createEnglishCache();
    wantedRef.current = [];
    setModeBoth(TRANSLATE_MODE_ORIGINAL);
    setStatus(TRANSLATE_STATUS_IDLE);
    setProgress(EMPTY_PROGRESS);
    setError(null);
    bump();
  }, [triviaId, endSession, setModeBoth, bump]);

  const readTranslations = useCallback(
    (ids) =>
      axios
        .get(url.getResponseTranslations(triviaId), {
          params: { resultIds: ids.join(",") },
          timeout: TRANSLATE_REQUEST_TIMEOUT_MS,
        })
        .then(unwrapData),
    [triviaId]
  );

  const processTranslations = useCallback(
    (body) =>
      axios
        .post(url.processResponseTranslations(triviaId), body, { timeout: TRANSLATE_REQUEST_TIMEOUT_MS })
        .then(unwrapData),
    [triviaId]
  );

  // Starts the scheduler unless it is already running; a running one picks up wantedRef on its
  // next step, so there is only ever one.
  const ensureRunning = useCallback(() => {
    if (!triviaId || runningRef.current || modeRef.current !== TRANSLATE_MODE_ENGLISH) {
      return;
    }
    if (isWindowSettled(cacheRef.current, wantedRef.current)) {
      setStatus(TRANSLATE_STATUS_DONE);
      return;
    }
    const session = sessionRef.current;
    const isCancelled = () =>
      session !== sessionRef.current || !mountedRef.current || modeRef.current !== TRANSLATE_MODE_ENGLISH;
    runningRef.current = true;
    setStatus(TRANSLATE_STATUS_RUNNING);
    setError(null);
    // Async callbacks run outside React events: batch each update into one render.
    const onUpdate = (patch) => {
      if (isCancelled()) {
        return;
      }
      unstable_batchedUpdates(() => {
        if (patch.read && mergeRead(cacheRef.current, patch.read) > 0) {
          bump();
        }
        if (Object.prototype.hasOwnProperty.call(patch, "notice")) {
          setProgress({ notice: patch.notice });
        }
      });
    };
    const deps = {
      process: processTranslations,
      read: readTranslations,
      cache: cacheRef.current,
      getWindowIds: () => wantedRef.current,
      wait: wait,
      isCancelled: isCancelled,
      onUpdate: onUpdate,
    };
    const finish = (result) => {
      if (isCancelled()) {
        return;
      }
      runningRef.current = false;
      unstable_batchedUpdates(() => {
        setProgress(EMPTY_PROGRESS);
        if (result.outcome === TRANSLATE_OUTCOME_DONE) {
          setStatus(TRANSLATE_STATUS_DONE);
          return;
        }
        setStatus(TRANSLATE_STATUS_ERROR);
        setError({ kind: result.kind || TRANSLATE_ERROR_GENERIC });
        if (result.outcome === TRANSLATE_OUTCOME_REVERT) {
          endSession();
          setModeBoth(TRANSLATE_MODE_ORIGINAL);
        }
      });
      // The window moved while the last step was finishing: carry on with the new one.
      if (
        result.outcome === TRANSLATE_OUTCOME_DONE &&
        result.windowSignature !== signatureOf(wantedRef.current) &&
        !isWindowSettled(cacheRef.current, wantedRef.current)
      ) {
        ensureRunning();
      }
    };
    runWindowPass(deps, createRunState()).then(
      (result) => {
        if (result.outcome !== TRANSLATE_OUTCOME_CANCELLED) {
          finish(result);
        }
      },
      () => finish({ outcome: TRANSLATE_OUTCOME_ERROR, kind: TRANSLATE_ERROR_GENERIC })
    );
  }, [triviaId, processTranslations, readTranslations, wait, bump, endSession, setModeBoth]);

  // Scrolling, resizing or new rows: after a 0.4 s pause the current window becomes the target.
  const scheduleWindow = useCallback(() => {
    if (modeRef.current !== TRANSLATE_MODE_ENGLISH) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      if (!mountedRef.current || modeRef.current !== TRANSLATE_MODE_ENGLISH) {
        return;
      }
      wantedRef.current = currentWindowIds();
      ensureRunning();
    }, TRANSLATE_WINDOW_DEBOUNCE_MS);
  }, [currentWindowIds, ensureRunning]);

  useEffect(() => {
    scheduleWindow();
  }, [rowsSignature, scheduleWindow]);

  const setMode = useCallback(
    (next) => {
      if (next === TRANSLATE_MODE_ENGLISH) {
        if (modeRef.current === TRANSLATE_MODE_ENGLISH) {
          return;
        }
        setModeBoth(TRANSLATE_MODE_ENGLISH);
        setError(null);
        // Every row shows at once (cached English where held, original otherwise).
        bump();
        wantedRef.current = currentWindowIds();
        ensureRunning();
        return;
      }
      endSession();
      setModeBoth(TRANSLATE_MODE_ORIGINAL);
      setStatus(TRANSLATE_STATUS_IDLE);
      setError(null);
      setProgress(EMPTY_PROGRESS);
    },
    [currentWindowIds, ensureRunning, endSession, setModeBoth, bump]
  );

  const retry = useCallback(() => {
    wantedRef.current = currentWindowIds();
    ensureRunning();
  }, [currentWindowIds, ensureRunning]);

  // Rows a By User endpoint returned with displayLanguage=en (filter changes in English).
  const ingestRows = useCallback(
    (rows, meta) => {
      if (modeRef.current !== TRANSLATE_MODE_ENGLISH) {
        return;
      }
      if (mergeRows(cacheRef.current, rows, meta) > 0) {
        bump();
      }
    },
    [bump]
  );

  // Original shows the source text; English shows cached English where it exists.
  const active = mode === TRANSLATE_MODE_ENGLISH;
  const getText = useCallback(
    (resultId, questionId, original) => (active ? pickText(cacheRef.current, resultId, questionId, original) : original),
    [active, version]
  );
  const getQuestionText = useCallback(
    (questionId, original) => (active ? pickQuestionText(cacheRef.current, questionId, original) : original),
    [active, version]
  );
  const getOptionText = useCallback(
    (questionId, optionId, original) =>
      active ? pickOptionText(cacheRef.current, questionId, optionId, original) : original,
    [active, version]
  );
  const getScaleLabel = useCallback(
    (questionId, end, original) => (active ? pickScaleLabel(cacheRef.current, questionId, end, original) : original),
    [active, version]
  );
  const getMessageText = useCallback(
    (field, original) => (active ? pickMessageText(cacheRef.current, field, original) : original),
    [active, version]
  );

  return {
    mode: mode,
    setMode: setMode,
    status: status,
    progress: progress,
    error: error,
    retry: retry,
    masked: active && cacheRef.current.masked,
    active: active,
    version: version,
    scheduleWindow: scheduleWindow,
    ingestRows: ingestRows,
    getText: getText,
    getQuestionText: getQuestionText,
    getOptionText: getOptionText,
    getScaleLabel: getScaleLabel,
    getMessageText: getMessageText,
  };
};

export default useTranslatedResponses;
